<template>
  <v-autocomplete
    :value="value"
    :items="items"
    :loading="loading"
    :search-input.sync="query"
    :label="label"
    :rules="rules"
    :hint="$t('intranet.homeContent.picker.hint')"
    persistent-hint
    item-text="label"
    item-value="id"
    :no-data-text="noDataText"
    :no-filter="true"
    clearable
    return-object
    @change="$emit('input', $event)"
  ></v-autocomplete>
</template>

<script>
import { Wine, errorHandler } from 'aveine-toolbox'

/**
 * Selection d'un vin par recherche (REDMINE-12962).
 *
 * Ecrit ici parce qu'aucun selecteur de vin reutilisable n'existait : le paquet
 * aveine-wine expose des listes et des fiches, jamais un champ de choix.
 *
 * Un vin n'a pas de nom : `cuvee` et le domaine vivent sur son TEMPLATE, seul
 * le millesime lui appartient. La recherche porte donc sur `template.cuvee` et
 * `template.domain.name`, et le libelle est recompose a l'affichage.
 */
export default {
  name: 'AveineWinePicker',
  props: {
    /** Le vin choisi ({ id, label }), ou null. */
    value: { type: Object, default: null },
    label: { type: String, required: true },
    required: { type: Boolean, default: false },
    APIbaseURL: String
  },
  data: () => ({
    entries: [],
    loading: false,
    query: null,
    debounce: null
  }),
  computed: {
    /**
     * Le vin deja choisi est injecte dans la liste : sans lui, v-autocomplete
     * n'a rien a afficher a l'ouverture d'un formulaire de modification et le
     * champ paraitrait vide alors qu'un vin est bien selectionne.
     */
    items () {
      const entries = this.entries.slice()
      if (this.value && !entries.some(entry => entry.id === this.value.id)) {
        entries.unshift(this.value)
      }
      return entries
    },
    rules () {
      return this.required
        ? [v => !!v || this.$t('intranet.homeContent.validation.wineRequired')]
        : []
    },
    noDataText () {
      return this.query && this.query.length >= 2
        ? this.$t('intranet.homeContent.picker.noWine')
        : this.$t('intranet.homeContent.picker.typeToSearch')
    }
  },
  watch: {
    query (value) {
      // Deux caracteres minimum : en dessous, la recherche ramene un bruit
      // inexploitable sur un catalogue de pres de cent mille vins.
      if (!value || value.length < 2) return
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => this.search(value), 300)
    }
  },
  beforeDestroy () {
    // Sinon une frappe suivie d'une fermeture immediate laisse une requete
    // partir dans le vide et ecrire dans un composant demonte.
    clearTimeout(this.debounce)
  },
  methods: {
    /** « Château Margaux — Margaux Grand Cru Classé 2012 ». */
    describe (wine) {
      const template = wine.template || {}
      const domain = template.domain || {}
      return [
        domain.name,
        // Beaucoup de domaines nomment leur cuvee comme eux-memes : on evite
        // « Château Margaux Château Margaux 1984 ».
        template.cuvee === domain.name ? null : template.cuvee,
        wine.vintage
      ].filter(Boolean).join(' ') || this.$t('intranet.homeContent.picker.unnamedWine')
    },

    async search (value) {
      this.loading = true
      try {
        // `=ilike=` et surtout PAS `=re=` : le parseur de creddy ne connait que
        // gt/ge/lt/le/like/unlike/ilike/unilike/between/in/out, et un operateur
        // inconnu n'echoue pas — l'API repond 200, pose un avertissement dans
        // meta.warnings et IGNORE le filtre. La recherche ramenerait alors tout
        // le catalogue, en silence. Lecon du ticket 12958.
        //
        // `,` est le OU de FIQL, `;` le ET. Les jokers saisis sont echappes,
        // sinon un `%` elargirait la recherche au lieu de la restreindre.
        const term = String(value).replace(/[%_\\]/g, m => '\\' + m)
        const response = await Wine.api().get(
          `wines?lang=${this.$i18n.locale}`,
          {
            baseURL: this.APIbaseURL,
            params: {
              include: 'template.domain',
              filter: `template.cuvee=ilike=%${term}%,template.domain.name=ilike=%${term}%`,
              'page[size]': 20
            }
          }
        )

        // `withAllRecursive` plutot que de recoudre le `included` du document a
        // la main : c'est l'idiome du depot (voir unchecked-wines/Index.vue),
        // et vuex-orm a deja normalise la reponse.
        this.entries = (response.entities ? response.entities.wines || [] : []).map(wine => {
          const full = Wine.query().whereId(wine.id).withAllRecursive().first() || wine
          return { id: wine.id, label: this.describe(full) }
        })
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
